import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ platform_name: "Protect & Fund" });
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgIdState] = useState(null);

  const loadOrgs = useCallback(async (u) => {
    if (!u || u.mfa_pending) return [];
    const { data } = await api.get("/orgs");
    setOrgs(data);
    const stored = localStorage.getItem(`pf_org_${u.user_id}`);
    setOrgIdState(data.find((o) => o.id === stored) ? stored : data[0]?.id || null);
    return data;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadOrgs(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadOrgs]);

  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
    // Skip /me while returning from OAuth; AuthCallback establishes the session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const setOrgId = (id) => {
    setOrgIdState(id);
    if (user) localStorage.setItem(`pf_org_${user.user_id}`, id);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setOrgs([]);
    window.location.href = "/";
  };

  const org = orgs.find((o) => o.id === orgId) || null;
  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout, settings, setSettings, orgs, org, orgId, setOrgId, loadOrgs: () => loadOrgs(user) }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

export const startLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
